import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  ListChecks, Loader2, RefreshCw, Plus, Trash2, PenTool, ShieldCheck,
  AlertTriangle, CheckCircle2, CircleDashed,
} from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

const SEV_CLASS = {
  high: 'border-rose-500 text-rose-700 dark:text-rose-400',
  medium: 'border-amber-500 text-amber-700 dark:text-amber-400',
  low: 'border-slate-400 text-slate-600 dark:text-slate-300',
};
const SOURCE_LABEL = {
  intake: 'Draft audit', readiness: 'Readiness', wave_d: 'Analysis',
  defense: 'Defense panel', manual: 'Manual',
};
const FILTERS = [
  ['open', 'Open'], ['high', 'High priority'], ['needs_work', 'Needs work'],
  ['done', 'Done'], ['all', 'All'],
];

const matches = (t, filter) => {
  if (filter === 'all') return true;
  if (filter === 'done') return t.status === 'done';
  if (filter === 'needs_work') return t.status === 'needs_work';
  if (filter === 'high') return t.severity === 'high' && t.status !== 'done';
  return t.status !== 'done' && t.status !== 'dismissed';
};

export default function WritingPlanPanel({ userId, projectId, onGoToChapter }) {
  const [tasks, setTasks] = useState([]);
  const [prog, setProg] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState('open');
  const [newTitle, setNewTitle] = useState('');
  const [verifying, setVerifying] = useState({});      // taskId -> true
  const timers = useRef([]);

  const load = useCallback(async () => {
    try {
      const res = await thesisApi.listPlanTasks(userId, projectId);
      setTasks(res.tasks || []);
      setProg(res.progress || null);
      setChapters(res.chapters || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [userId, projectId]);

  useEffect(() => {
    load();
    return () => { timers.current.forEach(clearInterval); timers.current = []; };
  }, [load]);

  const patch = async (task, payload) => {
    try {
      const updated = await thesisApi.updatePlanTask(userId, task.id, payload);
      setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, ...updated } : t)));
      const res = await thesisApi.listPlanTasks(userId, projectId);
      setProg(res.progress || null);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const verify = async (task) => {
    setVerifying((v) => ({ ...v, [task.id]: true }));
    setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, status: 'verifying' } : t)));
    try {
      const { job_id } = await thesisApi.verifyPlanTask(userId, task.id);
      const iv = setInterval(async () => {
        try {
          const j = await thesisApi.planJob(userId, job_id);
          if (j.status === 'ready') {
            clearInterval(iv);
            setVerifying((v) => ({ ...v, [task.id]: false }));
            const { status, verification } = j.result || {};
            setTasks((prev) => prev.map((t) => (t.id === task.id
              ? { ...t, status, verification } : t)));
            const res = await thesisApi.listPlanTasks(userId, projectId);
            setProg(res.progress || null);
            if (status === 'done') toast.success('Verified — the task is genuinely closed');
            else toast.warning('Not closed yet — see what is still missing');
          } else if (j.status === 'failed') {
            clearInterval(iv);
            setVerifying((v) => ({ ...v, [task.id]: false }));
            toast.error(j.error || 'Verification failed');
            load();
          }
        } catch (e) {
          clearInterval(iv);
          setVerifying((v) => ({ ...v, [task.id]: false }));
          toast.error(e.response?.data?.detail || e.message);
        }
      }, 2500);
      timers.current.push(iv);
    } catch (e) {
      setVerifying((v) => ({ ...v, [task.id]: false }));
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await thesisApi.syncPlanTasks(userId, projectId);
      setTasks(res.tasks || []);
      setProg(res.progress || null);
      toast.success(res.added_total
        ? `${res.added_total} new tasks imported from your analyses`
        : 'No new findings — your plan is up to date');
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSyncing(false); }
  };

  const addTask = async () => {
    if (newTitle.trim().length < 4) return;
    try {
      const t = await thesisApi.createPlanTask(userId, projectId, { title: newTitle.trim() });
      setTasks((prev) => [...prev, t]);
      setNewTitle('');
      const res = await thesisApi.listPlanTasks(userId, projectId);
      setProg(res.progress || null);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const remove = async (task) => {
    try {
      await thesisApi.deletePlanTask(userId, task.id);
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
      const res = await thesisApi.listPlanTasks(userId, projectId);
      setProg(res.progress || null);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const visible = tasks.filter((t) => matches(t, filter))
    .sort((a, b) => (a.priority - b.priority)
      || String(a.section).localeCompare(String(b.section)));

  return (
    <div className="space-y-4" data-testid="writing-plan-panel">
      <Card>
        <CardHeader>
          <CardTitle className="font-serif flex flex-wrap items-center gap-2"
            style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
            <ListChecks className="w-5 h-5 text-[#1d4ed8]" /> Writing plan
            {prog && <Badge variant="outline" className="text-[10px]" data-testid="plan-progress-badge">
              {prog.done}/{prog.total} done · {prog.percent}%
            </Badge>}
          </CardTitle>
          <CardDescription>
            Every gap found by the draft audit, the readiness score, the analysis engines and the
            defense panel becomes a writing task here. Tick one off and InkPhD re-reads the section
            to confirm it is genuinely closed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {prog && (
            <>
              <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all duration-700"
                  style={{ width: `${prog.percent}%` }} data-testid="plan-progress-bar" />
              </div>
              <div className="flex flex-wrap gap-2 text-[11px]">
                <Badge variant="outline">{prog.total} tasks</Badge>
                <Badge variant="outline" className="border-emerald-500 text-emerald-700 dark:text-emerald-400">
                  {prog.done} done
                </Badge>
                <Badge variant="outline" className="border-rose-500 text-rose-700 dark:text-rose-400">
                  {prog.open_high} high priority open
                </Badge>
                {!!prog.needs_work && (
                  <Badge variant="outline" className="border-amber-500 text-amber-700 dark:text-amber-400">
                    {prog.needs_work} need more work
                  </Badge>
                )}
              </div>
            </>
          )}

          {!!prog?.sections?.length && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="plan-sections">
              {prog.sections.map((s) => (
                <button key={s.section} type="button"
                  onClick={() => s.chapter_id && onGoToChapter?.(s.chapter_id)}
                  className="text-left p-2 rounded border border-border hover:border-[#1d4ed8]/50 transition-colors"
                  data-testid={`plan-section-${s.section.slice(0, 24)}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[12px] font-medium truncate">{s.section}</span>
                    <span className="text-[11px] tabular-nums text-muted-foreground">
                      {s.done}/{s.total}
                    </span>
                  </div>
                  <div className="mt-1.5 h-1 w-full rounded-full bg-muted overflow-hidden">
                    <div className={`h-full ${s.percent === 100 ? 'bg-emerald-500' : 'bg-[#1d4ed8]'}`}
                      style={{ width: `${Math.max(s.percent, 2)}%` }} />
                  </div>
                  {!!s.high && (
                    <div className="mt-1 text-[10px] text-rose-600">{s.high} high priority</div>
                  )}
                </button>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {FILTERS.map(([key, label]) => (
              <Button key={key} size="sm" variant={filter === key ? 'default' : 'outline'}
                onClick={() => setFilter(key)} data-testid={`plan-filter-${key}`}
                className="text-[11px] h-7">
                {label}
              </Button>
            ))}
            <div className="flex-1" />
            <Button size="sm" variant="outline" onClick={sync} disabled={syncing}
              data-testid="plan-sync-btn">
              {syncing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
              Import from analyses
            </Button>
          </div>

          <div className="flex gap-2">
            <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addTask()}
              placeholder="Add your own task — e.g. 'Add ethics approval number to Methods'"
              className="h-9 text-sm" data-testid="plan-new-task-input" />
            <Button size="sm" onClick={addTask} disabled={newTitle.trim().length < 4}
              data-testid="plan-add-task-btn" className="h-9">
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading your plan…
            </div>
          )}

          {!loading && !visible.length && (
            <div className="p-6 text-center text-sm text-muted-foreground border border-dashed rounded-lg"
              data-testid="plan-empty">
              {tasks.length
                ? 'Nothing in this filter. Switch to “All” to see every task.'
                : 'No tasks yet. Upload a draft in Workspace, or run a readiness audit, then press “Import from analyses”.'}
            </div>
          )}

          <div className="space-y-2">
            {visible.map((t) => {
              const done = t.status === 'done';
              const pending = t.status === 'verifying' || verifying[t.id];
              return (
                <div key={t.id} data-testid={`plan-task-${t.id}`}
                  className={`p-3 rounded-lg border space-y-1.5 ${done
                    ? 'border-emerald-500/40 bg-emerald-500/[0.04]'
                    : t.status === 'needs_work' ? 'border-amber-500/50 bg-amber-500/[0.04]'
                      : 'border-border'}`}>
                  <div className="flex items-start gap-2.5">
                    <button type="button" className="mt-0.5 shrink-0" disabled={pending}
                      title={done ? 'Reopen this task' : 'Mark done — InkPhD will verify it'}
                      onClick={() => (done ? patch(t, { status: 'todo' }) : verify(t))}
                      data-testid={`plan-task-toggle-${t.id}`}>
                      {pending ? <Loader2 className="w-4 h-4 animate-spin text-[#1d4ed8]" />
                        : done ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          : <CircleDashed className="w-4 h-4 text-muted-foreground" />}
                    </button>
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className={`text-[13px] font-medium ${done ? 'line-through text-muted-foreground' : ''}`}>
                          {t.title}
                        </span>
                        <Badge variant="outline" className={`text-[9px] ${SEV_CLASS[t.severity] || SEV_CLASS.low}`}>
                          {t.severity}
                        </Badge>
                        <Badge variant="outline" className="text-[9px]">
                          {SOURCE_LABEL[t.source] || t.source}
                        </Badge>
                        {t.section && (
                          <Badge variant="outline" className="text-[9px] max-w-[180px] truncate">
                            {t.section}
                          </Badge>
                        )}
                      </div>
                      {t.detail && <p className="text-[11px] text-muted-foreground">{t.detail}</p>}
                      {t.fix && (
                        <p className="text-[11px] text-[#1d4ed8] dark:text-[#7aa9ff]">Fix: {t.fix}</p>
                      )}
                      {t.verification && (
                        <div className={`text-[11px] p-2 rounded border ${t.verification.verified
                          ? 'border-emerald-500/40 text-emerald-700 dark:text-emerald-400'
                          : 'border-amber-500/40 text-amber-700 dark:text-amber-400'}`}
                          data-testid={`plan-task-verification-${t.id}`}>
                          <span className="inline-flex items-center gap-1 font-medium">
                            {t.verification.verified
                              ? <ShieldCheck className="w-3 h-3" />
                              : <AlertTriangle className="w-3 h-3" />}
                            {t.verification.verified ? 'Verified closed' : 'Still open'}
                          </span>
                          <span className="ml-1.5">{t.verification.rationale}</span>
                          {!!t.verification.still_missing?.length && (
                            <ul className="mt-1 list-disc list-inside">
                              {t.verification.still_missing.map((m, i) => <li key={i}>{m}</li>)}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {t.chapter_id && (
                        <Button size="icon" variant="ghost" className="h-7 w-7"
                          title="Open this section in the editor"
                          onClick={() => onGoToChapter?.(t.chapter_id, t)}
                          data-testid={`plan-task-write-${t.id}`}>
                          <PenTool className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {t.status === 'needs_work' && (
                        <Button size="sm" variant="ghost" className="h-7 text-[10px]"
                          onClick={() => patch(t, { status: 'done' })}
                          data-testid={`plan-task-force-done-${t.id}`}>
                          Done anyway
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground"
                        onClick={() => remove(t)} data-testid={`plan-task-delete-${t.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
